import { SimpleGrid, Stack, Text } from '@mantine/core';
import { formatCurrency, getTulsaShare, getTulsaShareQoQ, latest, previous } from '../types';
import type { SafeTOrgKey, TulsaPoint } from '../types';
import { SafeTCardShell } from './SafeTCardShell';
import { AppTooltip } from '../../../common/AppTooltip';

interface TulsaAllTime {
  totalRevenue: number;
  tulsaRevenue: number;
  totalProducers: number;
  tulsaProducers: number;
}

type Props =
  | { mode: 'combined'; series: TulsaPoint[]; tulsaAllTime: TulsaAllTime }
  | { mode: 'org'; orgKey: SafeTOrgKey; series: TulsaPoint[]; tulsaAllTime: TulsaAllTime };

export function TulsaImpactCard(props: Props) {
  const series = props.series;
  const cur = series.length > 0 ? latest(series) : null;
  const prev = previous(series);
  const share = cur ? getTulsaShare(cur.tulsaRevenue, cur.totalRevenue) : null;
  const shareQoQ = getTulsaShareQoQ(series);

  const status = { label: 'Snapshot', color: 'gray' as const };

  // Calculate QoQ for Total Revenue
  const totalRevenueQoQ =
    prev && cur && prev.totalRevenue !== 0 ? ((cur.totalRevenue - prev.totalRevenue) / prev.totalRevenue) * 100 : null;

  // Calculate Tulsa Producers QoQ
  const prevTulsaProducers = prev?.tulsaProducers ?? null;
  const tulsaProducersQoQ =
    prevTulsaProducers !== null && prevTulsaProducers !== 0 && cur
      ? ((cur.tulsaProducers - prevTulsaProducers) / prevTulsaProducers) * 100
      : null;

  const qoqArrow = (n: number): '↑' | '↓' | '' => (n > 0 ? '↑' : n < 0 ? '↓' : '');

  return (
    <SafeTCardShell
      title="Serving Greater Tulsa Region"
      subtitle={cur?.quarter ?? 'No data'}
      statusLabel={status.label}
      statusColor={status.color}
    >
      <SimpleGrid cols={3} spacing="lg" breakpoints={[{ maxWidth: 1200, cols: 2 }, { maxWidth: 700, cols: 1 }]}>
        <Stack spacing={2}>
          <AppTooltip label="Total revenue from all clients." inline>
            <Text
              size="xs"
              color="dimmed"
              component="span"
              sx={{
                cursor: 'help',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 2,
                display: 'inline-flex',
                width: 'fit-content',
              }}
            >
              Total Revenue
            </Text>
          </AppTooltip>
          <Text size="lg" weight={800}>
            {cur ? formatCurrency(cur.totalRevenue) : '—'}
          </Text>
          <Text size="xs" color="dimmed">
            {totalRevenueQoQ === null
              ? '— QoQ'
              : totalRevenueQoQ === 0
                ? '0.00% QoQ'
                : `${qoqArrow(totalRevenueQoQ)}${Math.abs(totalRevenueQoQ).toFixed(2)}% QoQ`}
          </Text>
        </Stack>

        <Stack spacing={2}>
          <AppTooltip label="Percentage of total revenue from clients that assemble or manufacture products in Tulsa MSA." inline>
            <Text
              size="xs"
              color="dimmed"
              component="span"
              sx={{
                cursor: 'help',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 2,
                display: 'inline-flex',
                width: 'fit-content',
              }}
            >
              Tulsa Share
            </Text>
          </AppTooltip>
          <Text size="lg" weight={800}>
            {share === null ? '—' : `${Math.round(share * 100)}%`}
          </Text>
          <Text size="xs" color="dimmed">
            {shareQoQ === null
              ? '— QoQ'
              : shareQoQ === 0
                ? '0.00% QoQ'
                : `${qoqArrow(shareQoQ)}${Math.abs(shareQoQ).toFixed(2)}% QoQ`}
          </Text>
        </Stack>

        <Stack spacing={2}>
          <AppTooltip label="Number of clients that assemble or manufacture products in Tulsa MSA." inline>
            <Text
              size="xs"
              color="dimmed"
              component="span"
              sx={{
                cursor: 'help',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 2,
                display: 'inline-flex',
                width: 'fit-content',
              }}
            >
              Tulsa Producers
            </Text>
          </AppTooltip>
          <Text size="lg" weight={800}>
            {cur?.tulsaProducers ?? '—'}
            <Text component="span" size="sm" weight={400} sx={{ verticalAlign: 'sub', fontSize: '0.6em' }}>
              {cur ? ` of ${cur.totalProducers}` : ''}
            </Text>
          </Text>
          <Text size="xs" color="dimmed">
            {tulsaProducersQoQ !== null
              ? tulsaProducersQoQ === 0
                ? '0.00% QoQ'
                : `${qoqArrow(tulsaProducersQoQ)}${Math.abs(tulsaProducersQoQ).toFixed(2)}% QoQ`
              : '— QoQ'}
          </Text>
        </Stack>
      </SimpleGrid>
    </SafeTCardShell>
  );
}
